'use client';

import Flag from 'components/Flag';
import MainHeader from 'components/HeaderNavigation';
import { TWContainer } from 'components/IngredientWrapper';
import { type INotificationBannerProps, NotificationBanner } from 'components/NotificationBanner';
import dynamic from 'next/dynamic';
import { type ReactNode, useEffect, useState } from 'react';
import getSearchClient from 'server/repository/algolia/client';

import type {
  IPopularSearch,
  ISearchOverlay,
} from 'components/HeaderNavigation/components/MobileNavigation';
import { MetaDataNextSEO, type MetaDataType } from 'components/MetaDataNextSEO/MetaDataNextSEO';
import { useRouter } from 'next/router';
import type { CountrySelectorFooter } from 'server/repository/hygraph/generated/graphqlTypes';
import type { GetNotificationBannerQuery } from 'server/repository/hygraph/generated/graphql';
import type { FooterNav } from 'types/Footer';
import type Navigation from 'types/Navigation';
import type { SearchCarousel } from 'types/SearchOverlay';
import type { MobileOnlyAsset } from 'types/headerNav';

import type { AVAILABLE_THEMES_TYPE } from '@/styles/constants';
import { CurveMask } from 'components/CurveMask';
import { TrackingService } from 'service/tracking/trackingService';
import type { ICountryLanguage } from '@/components/HeaderNavigation/types';
import { conditionalClasses } from '@/utils';
import { RouteProgress } from '../RouteProgress';

const Footer = dynamic(() => import('components/Footer').then((m) => m.Footer), {
  ssr: true,
});

const Newsletter = dynamic(() => import('components/Forms').then((m: any) => m.Newsletter), {
  ssr: true,
}) as any;

export interface RootLayoutClientProps {
  getHeaderNav: Navigation[];
  getMobileNavData: MobileOnlyAsset | null;
  popularSearchData: IPopularSearch;
  difficultyCopy: Record<string, string>;
  cardCarouselCMSData: SearchCarousel;
  globalMetadata: MetaDataType | null;
  locale: string;
  searchOverlayData: ISearchOverlay;
  footerNavData?: FooterNav;
  footerImage?: any;
  countrySelectorData?: CountrySelectorFooter[];
  newsletterData: any;
  contactUsUrl: string | null;
  children: ReactNode;
  recallData: GetNotificationBannerQuery['notificationBanners'][number];
  themeName?: AVAILABLE_THEMES_TYPE;
  isPromotionPage?: boolean;
}

export default function RootLayoutClient({
  getHeaderNav,
  getMobileNavData,
  popularSearchData,
  difficultyCopy,
  globalMetadata,
  cardCarouselCMSData,
  locale,
  searchOverlayData,
  footerNavData,
  footerImage,
  countrySelectorData,
  newsletterData,
  contactUsUrl,
  children,
  recallData,
  isPromotionPage,
  themeName,
}: RootLayoutClientProps) {
  const [searchOverlayDataExtended, setSearchOverlayDataExtended] = useState(searchOverlayData);
  useEffect(() => {
    TrackingService.pageValues(
      locale?.slice(3, 5) || '',
      locale?.slice(0, 2).toUpperCase() || '',
      window.location.pathname || '',
    );
  }, []);

  useEffect(() => {
    if (!searchOverlayDataExtended.searchClient) {
      const algoliaClient = getSearchClient();
      setSearchOverlayDataExtended({
        ...searchOverlayDataExtended,
        searchClient: algoliaClient,
      });
    }
  }, [searchOverlayData]);

  const footerCountrySelectorData = countrySelectorData?.[0];
  const countryLanguages = (countrySelectorData?.[0]?.countrySelectorList?.[0]?.language ??
    []) as ICountryLanguage[];

  const featuredCarousel = {
    carouselData: cardCarouselCMSData,
    cardData: [],
    copyDictionary: difficultyCopy,
  };

  // needs to be tested. track pageview on change
  const { asPath } = useRouter();
  useEffect(() => {
    TrackingService.pageview(window.location.href, isPromotionPage);
  }, [asPath, isPromotionPage]);

  return (
    <>
      <TWContainer
        twClasses={conditionalClasses({
          christmas_blue_edition: themeName === 'christmas_blue_edition',
          christmas: themeName === 'christmas',
          suprema: themeName === 'suprema',
        })}
      >
        <RouteProgress />

        {globalMetadata && <MetaDataNextSEO metaData={globalMetadata} locale={locale} />}

        <Flag locale={locale} url={contactUsUrl ?? ''} />
        <MainHeader
          headerNavData={getHeaderNav}
          searchData={searchOverlayDataExtended}
          mobileOnlyData={getMobileNavData}
          popularSearch={popularSearchData}
          featuredCardCarousel={featuredCarousel}
          logo={globalMetadata?.logo as { url: string }}
          countryLanguages={countryLanguages}
          locale={locale}
        />
        {recallData?.active && (
          <div className="max-w-6xl mx-auto">
            <NotificationBanner {...(recallData as unknown as INotificationBannerProps)} />
          </div>
        )}
        <div className="max-w-6xl mx-auto">{children}</div>
        {newsletterData && (
          <div className="mb-[-25vw] md:mb-[-20vw] lg:mb-[-200px]" data-print="hide">
            <Newsletter
              curveColor="#FDF9F"
              data={newsletterData}
              trackingEvents={{ trackSubmitTrueEvent: TrackingService.trackSubmitTrueEvent }}
            />
          </div>
        )}
        {footerNavData && (
          <Footer
            footerImage={footerImage}
            footerData={footerNavData}
            countrySelectorData={footerCountrySelectorData}
            isNewsletter={!!newsletterData}
            fillColorClass="bg-lightBeige fill-[#F7E9D5]"
            trackingEvents={{
              about: TrackingService.footerAbout,
              social: TrackingService.footerFollowUs,
              more: TrackingService.footerMore,
              appStore: TrackingService.footerAppStore,
            }}
          />
        )}
        <CurveMask />
        {/* TO NOT REMOVE THIS `<CurveMask />` COMPONENT, this is a global configuration for the svg masks.
      This should be the only place for this */}
      </TWContainer>
    </>
  );
}
